
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, ShoppingCart, Plus, Minus, ChevronLeft, ChevronRight } from 'lucide-react';
import { Product } from '@/types/product';
import { useFavorites } from '@/hooks/useFavorites';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { imageUrl } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

interface ProductQuickViewProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart?: (product: Product) => void;
}

const ProductQuickView: React.FC<ProductQuickViewProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart
}) => {
  const { t } = useLanguage();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get all images (main + additional)
  const allImages = [product?.image, ...(product?.additionalImages || [])].filter(Boolean) as string[];

  if (!product) return null;

  const handleFavoriteToggle = () => {
    toggleFavorite(product.id);
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    // Toast is now handled in CartContext
  };

  const incrementQuantity = () => {
    setQuantity(prev => Math.min(prev + 1, 10)); // Limite máximo de 10
  };

  const decrementQuantity = () => {
    setQuantity(prev => Math.max(prev - 1, 1)); // Mínimo de 1
  };

  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-4xl max-h-[90vh] bg-white border-0 overflow-hidden p-0 top-[5vh] translate-y-0 sm:top-[50%] sm:translate-y-[-50%] flex flex-col">
        <DialogHeader className="px-4 pt-4 md:px-6 md:pt-6 pb-2 md:pb-2 flex-shrink-0">
          <DialogTitle className="sr-only">
            {t('products.quickView')}: {product.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 overflow-y-auto px-4 pb-4 md:px-6 md:pb-6">
          {/* Product Image Gallery */}
          <div className="space-y-2 md:space-y-4 flex-shrink-0">
            {/* Main Image */}
            <div className="relative">
              <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden max-h-[250px] md:max-h-none flex items-center justify-center">
                <img
                  src={imageUrl(allImages[currentImageIndex])}
                  alt={product.name}
                  className="max-w-full max-h-full w-auto h-auto object-contain p-4 md:p-6"
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    // Prevent infinite loop by checking if we're already showing the fallback
                    if (!target.src.includes('60244da3-6deb-4628-b29a-c8e90866808e.png')) {
                      target.src = imageUrl('/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png');
                    } else {
                      // If even the fallback fails, hide image and show placeholder
                      target.style.display = 'none';
                      target.parentElement?.classList.add('bg-gray-200');
                    }
                  }}
                />
              </div>

              {/* Navigation arrows - only show if multiple images */}
              {allImages.length > 1 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prevImage}
                    className="absolute left-1 md:left-2 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-50 border-2 border-gray-400 shadow-lg text-gray-700 hover:text-brand-red hover:border-brand-red transition-all duration-200 h-8 w-8 md:h-10 md:w-10 p-0"
                  >
                    <ChevronLeft size={16} className="md:w-5 md:h-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={nextImage}
                    className="absolute right-1 md:right-2 top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-50 border-2 border-gray-400 shadow-lg text-gray-700 hover:text-brand-red hover:border-brand-red transition-all duration-200 h-8 w-8 md:h-10 md:w-10 p-0"
                  >
                    <ChevronRight size={16} className="md:w-5 md:h-5" />
                  </Button>
                </>
              )}
            </div>

            {/* Image Thumbnails - only show if multiple images */}
            {allImages.length > 1 && (
              <div className="flex gap-1 md:gap-2 overflow-x-auto pb-1 md:pb-2 scrollbar-thin scrollbar-thumb-brand-red scrollbar-track-gray-200">
                {allImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-10 h-10 md:w-16 md:h-16 rounded-md overflow-hidden border-2 transition-colors ${
                      index === currentImageIndex ? 'border-brand-red ring-1 ring-brand-red/30' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={imageUrl(image)}
                      alt={`${product.name} - ${t('products.image')} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        // Prevent infinite loop by checking if we're already showing the fallback
                        if (!target.src.includes('60244da3-6deb-4628-b29a-c8e90866808e.png')) {
                          target.src = imageUrl('/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png');
                        } else {
                          // If even the fallback fails, hide image
                          target.style.display = 'none';
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-2 md:space-y-6">
            <div>
              <h1 className="text-base md:text-2xl font-bold text-gray-900 mb-1 md:mb-2 leading-tight">
                {product.name}
              </h1>

              {product.description && (
                <p className="text-gray-600 leading-snug text-[11px] md:text-base line-clamp-2 md:line-clamp-none">
                  {product.description}
                </p>
              )}
            </div>

            {/* Price */}
            <div className="space-y-0.5 md:space-y-2">
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3">
                <span className="text-lg md:text-3xl font-bold text-brand-red">
                  {product.pointsPrice * quantity} storepoints
                </span>
                {product.price && product.price > 0 && (
                  <span className="text-xs md:text-lg text-gray-500 line-through">
                    €{(product.price * quantity).toFixed(2)}
                  </span>
                )}
              </div>
              {quantity > 1 && (
                <p className="text-[10px] md:text-sm text-gray-500">
                  {product.pointsPrice} {t('products.storepointsPerUnit')}
                </p>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="space-y-2">
              <label className="text-xs md:text-sm font-medium text-gray-700">
                {t('products.quantity')}
              </label>
              <div className="flex items-center gap-2 md:gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={decrementQuantity}
                  disabled={quantity <= 1}
                  className="h-11 w-11 md:h-10 md:w-10 p-0 border-gray-300 hover:border-brand-red hover:text-brand-red"
                >
                  <Minus size={18} className="md:w-4 md:h-4" />
                </Button>
                <span className="font-medium text-base md:text-lg min-w-[2.5rem] md:min-w-[2rem] text-center bg-gray-100 px-3 py-2 md:px-3 md:py-2 rounded border text-black">
                  {quantity}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={incrementQuantity}
                  disabled={quantity >= 10}
                  className="h-11 w-11 md:h-10 md:w-10 p-0 border-gray-300 hover:border-brand-red hover:text-brand-red"
                >
                  <Plus size={18} className="md:w-4 md:h-4" />
                </Button>
              </div>
            </div>

            {/* Stock Badge */}
            <Badge
              variant={product.stock > 0 ? "secondary" : "destructive"}
              className="w-fit text-[10px] md:text-sm px-2 py-0.5 md:px-2.5 md:py-1"
            >
              {product.stock > 0 ? `${t('products.inStock')} (${product.stock})` : t('products.outOfStock')}
            </Badge>

            {/* Action Buttons */}
            <div className="flex md:flex-col gap-2 pt-2 md:pt-0">
              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                className="flex-1 md:w-full bg-brand-red hover:bg-brand-red/90 h-12 md:h-12 text-base md:text-base font-semibold gap-2"
                title={t('products.addToCart')}
              >
                <ShoppingCart size={20} className="md:w-5 md:h-5" />
                <span className="md:inline">{t('products.addToCart')}</span>
              </Button>

              {/* Favorites Button */}
              <Button
                variant="outline"
                onClick={handleFavoriteToggle}
                className={`flex-1 md:w-full h-12 md:h-12 text-base md:text-base gap-2 ${isFavorite(product.id)
                  ? 'border-red-500 text-red-500 bg-red-50 hover:bg-red-100'
                  : 'border-gray-300 text-gray-900 bg-white hover:bg-gray-50 hover:border-red-400 hover:text-red-500'
                }`}
                title={isFavorite(product.id) ? t('products.removeFromFavorites') : t('products.addToFavorites')}
              >
                <Heart
                  size={20}
                  className={`md:w-5 md:h-5 ${isFavorite(product.id) ? 'fill-current' : ''}`}
                />
                <span className="hidden sm:inline">{isFavorite(product.id) ? t('products.removeFromFavorites') : t('products.addToFavorites')}</span>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductQuickView;
